import React, { lazy, Suspense, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { AdminLayout } from "../_metronic/layout/components/AdminLayout";
import ProtectedRoute from "./base/components/ProtectedRoute";
import AuthService, { LogoutReason } from "./base/services/authentication.service";
import { AuthPage } from "./pages/auth/routes";
import { UserRolesEnum } from "./pages/auth/userRoles.enum";
import { Error500 } from "./pages/errorsExamples/Error500";
import { Error404 } from "./pages/errorsExamples/Error404";
import { Error401 } from "./pages/errorsExamples/Error401";
import Workspace from "./pages/workspace/index";
import Test from "./pages/admin/workspace/Test";
import EditPage from "./pages/workspace/components/newWorkSpace/EditPage";
import { NewWorkspace } from "./pages/workspace/components/newWorkSpace/NewWorkspace";
import { WorkspaceLayout } from "../_metronic/layout/components/WorkspaceLayout";
import SpaceSelectionPage from "./pages/workspace/space-selection";
import ProjectSelection from "./pages/workspace/projects/project-selection";
import { CreateNewProject } from "./pages/workspace/components/projects/CreateNewProject";
import ProjectTypes from "./pages/workspace/components/projects/createNewProject/ProjectTypes";
import AdminWorkspace from "./pages/admin/workspace/AdminWorkspace";
import UserManagement from "./pages/admin/user-management/UserManagement";
import PaymentPlans from "./pages/admin/payment-plans/PaymentPlans";
import { ComingSoon } from "./pages/comingSoon/ComingSoon";
import Preview from "./pages/surveyBuilder/v2/viewer/Preview/Preview";
import MyTasks from "./pages/workspace/my-tasks";
import { Spin } from "antd";
import OrganizationsPage from "./pages/admin/organizations/OrganizationsPage";
import ProfilePage from "./pages/auth/profile/ProfilePage";
import SubscriptionPage from "./pages/auth/subscription-plans/SubscriptionPage";
import LoginInformationPage from "./pages/auth/login-information/LoginInformationPage";
import AccountSummaryPage from "./pages/auth/account-summary/AccountSummaryPage";
import AdminDeletedItems from "./pages/admin/deleted-items/AdminDeletedItems";
import { withErrorBoundary } from "./components/ErrorBoundary/withErrorBoundary";
import { TicketsPage } from "./pages/auth/tickets/TicketsPage";
import TicketPreview from "./components/Tickets/TicketPreview";
import FundsPage from "./pages/auth/funds/FundsPage";
import BillingRequiredPage from "./components/Billing/BillingRequiredPage";
import CardSavedPage from "./components/Billing/CardSavedPage";
import { isBillingOnly, useBillingStore } from "./components/Billing/billingStore";
import {
   refreshSelectedOrganizationOnLoad,
   switchOrganization
} from "./components/Billing/billingSignIn";
import { isPublicSurveyRoute } from "./utils/constants";
import { usePageTitle } from "./hooks/usePageTitle";
import { useBusinessAccountRedirect } from "./hooks/useBusinessAccountRedirect";

const AdminPages = lazy(() => import("./pages/admin/routes"));
const WorkspacePages = lazy(() => import("./pages/workspace/routes"));
// The respondent view of a customer-feedback survey's public link. Lazy so the
// feedback kiosk isn't in everyone else's bundle.
const PublicFeedbackSurvey = lazy(() =>
   import("./pages/workspace/survey/feedback/PublicFeedbackSurvey")
);

function Routess() {
   const [isAuthenticated, setisAuth] = useState(() => AuthService.isAuthenticated());

   const location = useLocation();

   usePageTitle();

   // A business account is offered workspaces, projects, a project's dashboard
   // and customer feedback surveys; anything else it lands on sends it back.
   const businessRedirectPath = useBusinessAccountRedirect();

   // An organization held at billing_only by the new billing flow — a trial that
   // ended with no card, a payment that failed — is shown the billing page and
   // nothing else, to whoever may pay for it (members, below): every other call
   // it makes is refused with a 402. The store is
   // what says so (components/Billing/billingStore.js), and it is watched, so a
   // 402 in the middle of a session moves the page there on its own, and a card
   // saved moves it back. A 402 holds it there even while its status says it
   // is let in, until the two agree: otherwise they would trade the page back
   // and forth for as long as they disagree.
   const organizationId = AuthService.getOrganizationId();
   const billingStatus = useBillingStore((state) =>
      organizationId ? state.statuses[organizationId] : undefined
   );
   const isGateHeld = useBillingStore((state) =>
      Boolean(organizationId && state.gateHolds[organizationId])
   );
   const isBillingRequired = isAuthenticated && (isBillingOnly(billingStatus) || isGateHeld);
   // Who may open the Subscription page: selectOrganization says, not a role.
   const canManageBilling = useBillingStore((state) => state.canManageBilling);
   const navigate = useNavigate();

   // The billing page is only for whoever may pay. Anybody else in an
   // organization held at billing_only is kept out of it: with other
   // organizations, on the organization list, where this one shows locked;
   // with none, signed out, and the login form says why.
   const isLockedOut = isBillingRequired && !canManageBilling;
   const hasOtherOrganizations = (AuthService.getUser()?.organizations?.length ?? 0) > 1;
   // Not from a public survey, which a respondent is filling in whoever is signed in.
   const isSigningOutLocked = isLockedOut && !hasOtherOrganizations && !isPublicSurveyRoute();

   useEffect(() => {
      if (isSigningOutLocked) AuthService.logout(LogoutReason.ORGANIZATION_LOCKED);
   }, [isSigningOutLocked]);

   useEffect(() => {
      setisAuth(AuthService.isAuthenticated());
   }, [location, setisAuth]);

   // A session can outlive what it was signed in with: the billing flow switched
   // on, a new plan level, a change of who may manage billing. So the selected
   // organization is read again as the app starts. Not on a public survey, which
   // signs the browser out instead.
   useEffect(() => {
      const current = AuthService.getOrganizationId();
      if (!AuthService.isAuthenticated() || !current || isPublicSurveyRoute()) return;
      refreshSelectedOrganizationOnLoad(current);
   }, []);

   // A billing email links to /subscription-plans?organizationId=<id>. For
   // somebody in several organizations, that one is selected before the page
   // renders; one they aren't a member of sends them to the organization list.
   const linkedOrganizationId =
      isAuthenticated && location.pathname === "/subscription-plans"
         ? new URLSearchParams(location.search).get("organizationId")
         : null;
   const isSwitchingOrganization =
      Boolean(linkedOrganizationId) && linkedOrganizationId !== organizationId;
   const [, setSwitches] = useState(0);

   useEffect(() => {
      if (!isSwitchingOrganization) return undefined;
      let isCurrent = true;

      switchOrganization(linkedOrganizationId).then((opened) => {
         if (!isCurrent) return;
         if (!opened) navigate("/organizations", { replace: true });
         // The organization is read at render, so the routes look again.
         setSwitches((count) => count + 1);
      });

      return () => {
         isCurrent = false;
      };
   }, [isSwitchingOrganization, linkedOrganizationId, navigate]);

   const pageSpinner = (
      <Spin
         size="large"
         style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)"
         }}
      />
   );

   if (isSwitchingOrganization || isSigningOutLocked) return pageSpinner;

   if (businessRedirectPath) {
      return <Navigate to={businessRedirectPath} replace />;
   }

   return (
      <Suspense fallback={pageSpinner}>
         <Routes>
            {/* Public links. The feedback one is the same shape a segment
                deeper, so a survey shared as single-use / multi-use / kiosk
                means the same thing in either viewer. */}
            <Route path="/public-survey/feedback/:uuid/:id" element={<PublicFeedbackSurvey />} />
            <Route path="/public-survey/:uuid/:id" element={<Preview />} />

            {!isAuthenticated ? (
               /*Render auth page when user at `/auth` and not authorized.*/
               <Route path="/*" element={<AuthPage />} />
            ) : isLockedOut ? (
               /* A member of a held organization: their other organizations,
                  and nothing of this one. */
               <>
                  <Route path="/organizations" element={<OrganizationsPage />} />
                  <Route path="*" element={<Navigate to="/organizations" replace />} />
               </>
            ) : isBillingRequired ? (
               /* The billing page, where Stripe returns a card to, and the
                  organization list for somebody with another organization to
                  go to. Nothing else answers anyway. */
               <>
                  <Route path="/billing" element={<BillingRequiredPage />} />
                  <Route path="/billing/card-saved" element={<CardSavedPage />} />
                  <Route path="/organizations" element={<OrganizationsPage />} />
                  <Route path="*" element={<Navigate to="/billing" replace />} />
               </>
            ) : (
               /*Otherwise Navigate to root page (`/`)*/
               <>
                  {!AuthService.getOrganizationId() ||
                  !JSON.parse(localStorage.getItem("roles")) ? (
                     <Route path="/" element={<Navigate to="/organizations" />} />
                  ) : AuthService.isAuthorized([
                       UserRolesEnum.SuperAdmin,
                       UserRolesEnum.Organization,
                       UserRolesEnum.AdminOfOrganization,
                       UserRolesEnum.SeniorProjectManager
                    ]) ? (
                     <Route path="/" element={<Navigate to="/admin-workspaces" />} />
                  ) : AuthService.isAuthorized([UserRolesEnum.DataCollector]) ? (
                     <Route path="/" element={<Navigate to="/mytasks" />} />
                  ) : (
                     <Route path="/" element={<Navigate to="/workspace" />} />
                  )}

                  <Route path="/organizations" element={<OrganizationsPage />} />
                  {/* Nothing is held any more: the billing page hands back to
                      the app. A card whose bank check redirected still comes
                      back here to be completed. */}
                  <Route path="/billing" element={<Navigate to="/" replace />} />
                  <Route path="/billing/card-saved" element={<CardSavedPage />} />
                  <Route element={<AdminLayout />} /* Main Content with Layout */>
                     <Route path="/auth/*" element={<Navigate to="/" />} />
                     {/* module */}
                     <Route
                        element={
                           <ProtectedRoute
                              allowedRoles={[
                                 UserRolesEnum.SuperAdmin,
                                 UserRolesEnum.Organization,
                                 UserRolesEnum.JuniorProjectManager,
                                 UserRolesEnum.AdminOfOrganization,
                                 UserRolesEnum.SeniorProjectManager,
                                 UserRolesEnum.ProjectManager,
                                 UserRolesEnum.Guest
                              ]}
                           />
                        }
                     >
                        <Route path="/*" element={<WorkspacePages />} />
                     </Route>
                     {/* module */}
                  </Route>
                  <Route element={<AdminLayout />}>
                     <Route
                        element={
                           <ProtectedRoute
                              allowedRoles={[
                                 UserRolesEnum.SuperAdmin,
                                 UserRolesEnum.Organization,
                                 UserRolesEnum.AdminOfOrganization,
                                 UserRolesEnum.SeniorProjectManager
                              ]}
                           />
                        }
                     >
                        <Route path="/admin-workspaces" element={<AdminWorkspace />} />
                        <Route path="/admin-user-management" element={<UserManagement />} />
                        <Route path="/admin-deleted-items" element={<AdminDeletedItems />} />
                        {/* TODO: useless route */}
                        <Route path="/admin-payment-plans" element={<PaymentPlans />} />
                        <Route path="/workspace/new-work-space" element={<NewWorkspace />} />
                        <Route path="/workspace/edit/:id" element={<EditPage />} />{" "}
                        <Route path="/admin/*" element={<AdminPages />} />
                     </Route>
                  </Route>
                  <Route element={<AdminLayout />}>
                     {/* Whoever may manage the organization's billing
                         (canManageBilling), whatever their role. */}
                     <Route
                        path="/subscription-plans"
                        element={
                           canManageBilling ? (
                              <SubscriptionPage />
                           ) : (
                              <Navigate to="/unauthorized" replace />
                           )
                        }
                     />
                     <Route
                        element={<ProtectedRoute allowedRoles={[UserRolesEnum.Organization]} />}
                     >
                        <Route path="/usage" element={<AccountSummaryPage />} />
                        <Route path="/balance" element={<FundsPage />} />
                     </Route>
                     <Route
                        element={
                           <ProtectedRoute
                              allowedRoles={[
                                 UserRolesEnum.SuperAdmin,
                                 UserRolesEnum.Organization,
                                 UserRolesEnum.JuniorProjectManager,
                                 UserRolesEnum.AdminOfOrganization,
                                 UserRolesEnum.SeniorProjectManager,
                                 UserRolesEnum.ProjectManager
                                 // UserRolesEnum.Guest
                              ]}
                           />
                        }
                     >
                        <Route path="/profile" element={<ProfilePage />} />
                        <Route path="/login-information" element={<LoginInformationPage />} />
                     </Route>
                  </Route>
                  {/* module */}
                  <Route element={<ProtectedRoute allowedRoles={[UserRolesEnum.DataCollector]} />}>
                     <Route path="/mytasks" element={<MyTasks />} />
                  </Route>
                  <Route element={<WorkspaceLayout />}>
                     <Route
                        element={
                           <ProtectedRoute
                              allowedRoles={[
                                 UserRolesEnum.SuperAdmin,
                                 UserRolesEnum.Organization,
                                 UserRolesEnum.JuniorProjectManager,
                                 UserRolesEnum.AdminOfOrganization,
                                 UserRolesEnum.SeniorProjectManager,
                                 UserRolesEnum.ProjectManager,
                                 UserRolesEnum.Guest
                              ]}
                           />
                        }
                     >
                        <Route path="/workspace" element={<Workspace />} />
                        <Route path="/tickets" element={<TicketsPage />} />
                        <Route path="/tickets/:id" element={<TicketPreview />} />
                     </Route>
                  </Route>
                  <Route element={<WorkspaceLayout />}>
                     <Route
                        element={
                           <ProtectedRoute
                              allowedRoles={[
                                 UserRolesEnum.SuperAdmin,
                                 UserRolesEnum.Organization,
                                 UserRolesEnum.JuniorProjectManager,
                                 UserRolesEnum.AdminOfOrganization,
                                 UserRolesEnum.SeniorProjectManager,
                                 UserRolesEnum.ProjectManager,
                                 UserRolesEnum.Guest
                              ]}
                           />
                        }
                     >
                        <Route path="/workspace" element={<Workspace />} />
                        <Route path="/space-selection" element={<SpaceSelectionPage />} />
                        <Route path="/project-selection" element={<ProjectSelection />} />
                        <Route
                           path="/project-selection/create-project"
                           element={<CreateNewProject />}
                        />
                        <Route
                           element={
                              <ProtectedRoute
                                 allowedRoles={[
                                    UserRolesEnum.SuperAdmin,
                                    UserRolesEnum.Organization,
                                    UserRolesEnum.AdminOfOrganization,
                                    UserRolesEnum.SeniorProjectManager,
                                    UserRolesEnum.ProjectManager
                                 ]}
                              />
                           }
                           redirectPath="/"
                        >
                           {/* TODO: this route is not used because we don't have an edit button for projects in projects list */}
                           <Route
                              path="/project-selection/create-project/:id"
                              element={<ProjectTypes />}
                           />
                        </Route>
                        {/* TODO: useless route */}
                        <Route path="/workspace/test" element={<Test />} />
                        {/* TODO: useless route */}
                        <Route path="/workspace/add" element={<NewWorkspace />} />
                        {/* will add :id  */}
                     </Route>
                  </Route>
                  {/* module */}
               </>
            )}
            <Route path="/coming-soon" element={<ComingSoon />} />
            <Route path="/unauthorized" element={<Error401 />} />
            <Route path="/404" element={<Error404 />} />
            <Route path="/500" element={<Error500 />} />
            <Route path="*" element={<Error500 /> /* Catch All */} />
         </Routes>
      </Suspense>
   );
}
const RoutessWithErrorBoundary = withErrorBoundary(Routess);
export { RoutessWithErrorBoundary as Routess };
